import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { useAuthContext } from './AuthContext';
import { billingOnly } from '../services/institutionAccess';
const UnitContext = createContext(null);
let activeUnitId = null;
// Only send the unit to this application's API, never to third-party URLs.
export function applyUnitHeader(config) {
  const api = new URL(axios.defaults?.baseURL || window.location.origin, window.location.origin);
  const url = new URL(config.url || '', api);
  if (url.origin === api.origin && url.pathname.startsWith('/api/')) {
    config.headers = config.headers || {};
    if (activeUnitId) config.headers['X-Unit-Id'] = String(activeUnitId);
    else delete config.headers['X-Unit-Id'];
  }
  return config;
}
axios.interceptors.request.use(applyUnitHeader);
export function UnitProvider({ children }) {
  const { authState } = useAuthContext();
  const enabled = authState.isAuthenticated && authState.roles?.includes('clinica') && authState.onboarding?.complete && !billingOnly(authState);
  const identity = enabled ? `${authState.entidade?.id}:${authState.user?.id}:${authState.token}` : '';
  const [state, setState] = useState({ identity: '', loading: true, units: [], selected: null, error: false });
  const [retry, setRetry] = useState(0);
  const [pendingUnit, setPendingUnit] = useState(null);
  useEffect(() => {
    let live = true;
    setPendingUnit(null);
    activeUnitId = null;
    if (!enabled) { setState({ identity, loading: false, units: [], selected: null, error: false }); return () => { live = false; }; }
    setState({ identity, loading: true, units: [], selected: null, error: false });
    axios.get('/api/unit-context').then(r => {
      if (!live) return;
      const units = r.data.units || [];
      let saved = null;
      try { saved = sessionStorage.getItem(`unit:${authState.entidade?.id}:${authState.user?.id}`); } catch (_) {}
      const selected = units.find(u => String(u.id) === saved) || units.find(u => u.is_primary) || units[0] || null;
      activeUnitId = selected?.id || null;
      setState({ identity, loading: false, units, selected, navigationContext: r.data.navigation_context || 'clinica', multiunit: Boolean(r.data.multiunit_operation), crmReady: Boolean(r.data.crm_ready), crmOperationsReady: Boolean(r.data.crm_operations_ready), salesReady: Boolean(r.data.sales_ready), representativesReady: Boolean(r.data.representatives_ready), automationConfigurationReady: Boolean(r.data.automation_configuration_ready), fluxopayReady: Boolean(r.data.fluxopay_ready), commercialReady: Boolean(r.data.commercial_ready), clinicalReady: Boolean(r.data.clinical_ready), stockReady: Boolean(r.data.stock_ready), financeReady: Boolean(r.data.finance_ready), supportedModules: r.data.supported_modules || ['consultorio','crm','configuracoes'], error: false });
    }).catch(() => { if (live) setState({ identity, loading: false, units: [], selected: null, error: true }); });
    return () => { live = false; activeUnitId = null; };
  }, [identity, enabled, retry, authState.entidade?.id, authState.user?.id]);
  const select = id => {
    const selected = state.units.find(u => String(u.id) === String(id));
    if (!selected || selected.id === state.selected?.id) return;
    setPendingUnit({ id: selected.id, identity });
  };
  const confirmSwitch = () => {
    const selected = pendingUnit?.identity === identity && state.units.find(u => u.id === pendingUnit.id);
    setPendingUnit(null);
    if (!selected || selected.id === state.selected?.id) return;
    activeUnitId = selected.id;
    try { sessionStorage.setItem(`unit:${authState.entidade?.id}:${authState.user?.id}`, String(selected.id)); } catch (_) {}
    setState(current => ({ ...current, selected }));
  };
  if (enabled && (state.identity !== identity || state.loading)) return <div className="text-center p-5"><Spinner animation="border" /><p>Carregando unidades...</p></div>;
  if (enabled && state.error) return <Alert variant="danger" className="m-4">Falha ao carregar suas unidades. <Button onClick={() => setRetry(n => n + 1)}>Tentar novamente</Button></Alert>;
  const destination = pendingUnit?.identity === identity && state.units.find(u => u.id === pendingUnit.id);
  return <UnitContext.Provider value={{ ...state, enabled, select }}><React.Fragment key={enabled ? `${identity}:${state.selected?.id || 'none'}` : 'public'}>{children}</React.Fragment>
    <Modal show={Boolean(enabled && destination)} onHide={() => setPendingUnit(null)} centered aria-labelledby="unit-switch-title">
      <Modal.Header closeButton className="border-0 pb-0"><Modal.Title id="unit-switch-title" className="fs-5 fw-bold">Trocar unidade de trabalho</Modal.Title></Modal.Header>
      <Modal.Body className="px-4 pb-4">
        <p className="text-muted mb-4">Você está mudando o local de trabalho nesta sessão.</p>
        <div className="p-3 rounded-3 mb-3" style={{ background: '#f1f6fc', border: '1px solid #dce7f5' }}>
          <div className="small text-muted">Unidade atual</div><div className="fw-semibold mb-3">{state.selected?.nome}</div>
          <div className="small text-primary">Unidade de destino</div><div className="fw-bold fs-5" style={{ color: '#24568a' }}>{destination?.nome}</div>
        </div>
        <div className="small text-muted" style={{ lineHeight: 1.6 }}>As telas serão recarregadas com os dados da unidade escolhida. <strong>Alterações não salvas serão descartadas.</strong></div>
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0 px-4 pb-4"><Button variant="light" onClick={() => setPendingUnit(null)} autoFocus>Continuar nesta unidade</Button><Button variant="primary" onClick={confirmSwitch}>Trocar unidade</Button></Modal.Footer>
    </Modal>
  </UnitContext.Provider>;
}
export function useUnitContext() { return useContext(UnitContext); }
